import { useEffect, useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Title, Text, Grid, Card } from '@tremor/react';
import { useQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';

import { KPICard } from '@/components/KPICard';
import { StatusBadge } from '@/components/StatusBadge';
import { LoadingSkeleton } from '@/components/LoadingSkeleton';
import { api, buildQueryString } from '@/lib/api';
import { useDateRange } from '@/lib/dateRange';

interface CaseListItem {
  id: number;
  filename: string | null;
  num_processo: string | null;
  status: string | null;
  status_message: string | null;
  created_at: string | null;
  user_id: number | null;
  user_email: string | null;
  org_id: number | null;
  has_pdf: boolean;
  has_pjc: boolean;
  has_errors: boolean;
  thumb: string | null; // 'up' | 'down' | null — feedback do usuário
  baixou_memoria?: boolean; // usuário baixou o laudo/Memória de Cálculo deste caso?
  duration_s: number | null;
}

interface CaseListResponse {
  items: CaseListItem[];
  total: number;
  page: number;
  per_page: number;
}

interface KpiResponse {
  cases_today: number;
  cases_yesterday: number;
  success_rate_7d: number;
  success_rate_30d_avg: number;
  avg_processing_time_s: number | null;
  top_errors_7d: { error: string; count: number }[];
}

function useDebounced<T>(value: T, ms = 300): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function pct(n: number): string {
  return `${(n * 100).toFixed(1)}%`;
}

export function CasesPage() {
  const { range } = useDateRange();
  const [searchInput, setSearchInput] = useState('');
  const search = useDebounced(searchInput);
  const [statusFilter, setStatusFilter] = useState('');
  const [hasPdf, setHasPdf] = useState<boolean | undefined>(undefined);
  const [hasPjc, setHasPjc] = useState<boolean | undefined>(undefined);
  const [hasErrors, setHasErrors] = useState<boolean | undefined>(undefined);
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [useGlobalRange, setUseGlobalRange] = useState(true);
  const [page, setPage] = useState(1);

  // Resolve effective window (global takes precedence when toggle is on).
  const effDateFrom = useGlobalRange ? range.start.toISOString() : dateFrom;
  const effDateTo = useGlobalRange ? range.end.toISOString() : dateTo;
  const perPage = 50;
  const [selected, setSelected] = useState<Set<number>>(new Set());

  const kpiQuery = useQuery<KpiResponse>({
    queryKey: ['cases.kpis'],
    queryFn: async () => (await api().get('/cases/kpis')).data,
  });

  const listQuery = useQuery<CaseListResponse>({
    queryKey: [
      'cases.list',
      {
        search,
        statusFilter,
        hasPdf,
        hasPjc,
        hasErrors,
        effDateFrom,
        effDateTo,
        page,
      },
    ],
    queryFn: async () => {
      const qs = buildQueryString({
        page,
        per_page: perPage,
        q: search || undefined,
        status: statusFilter || undefined,
        has_pdf: hasPdf,
        has_pjc: hasPjc,
        has_errors: hasErrors,
        date_from: effDateFrom || undefined,
        date_to: effDateTo || undefined,
      });
      const r = await api().get(`/cases${qs}`);
      return r.data as CaseListResponse;
    },
  });

  const items = listQuery.data?.items ?? [];
  const total = listQuery.data?.total ?? 0;
  const lastPage = Math.max(1, Math.ceil(total / perPage));

  const todayDelta = useMemo(() => {
    if (!kpiQuery.data) return null;
    const t = kpiQuery.data.cases_today;
    const y = kpiQuery.data.cases_yesterday;
    if (y <= 0) return null;
    return (t - y) / y;
  }, [kpiQuery.data]);

  const successDelta = useMemo(() => {
    if (!kpiQuery.data) return null;
    return kpiQuery.data.success_rate_7d - kpiQuery.data.success_rate_30d_avg;
  }, [kpiQuery.data]);

  function toggleSelect(id: number) {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  }

  async function bulkRerun() {
    if (selected.size === 0) return;
    const ids = Array.from(selected);
    const results = await Promise.allSettled(
      ids.map((id) =>
        api().post(`/cases/${id}/rerun`, { from_phase: 'A' }),
      ),
    );
    setSelected(new Set());
    void listQuery.refetch();
    const failures = results.filter((r) => r.status === 'rejected').length;
    if (failures > 0) {
      // eslint-disable-next-line no-alert
      alert(`Rerun: ${failures} de ${ids.length} falharam.`);
    }
  }

  return (
    <div className="space-y-6">
      <header>
        <Title>Cases</Title>
        <Text>
          Pipeline de extração + Playwright + PDF. Use os filtros para isolar
          falhas, e a ação em massa para reprocessar várias execuções de uma
          vez.
        </Text>
      </header>

      <Grid numItems={1} numItemsSm={2} numItemsLg={4} className="gap-4">
        <KPICard
          label="Cases hoje"
          value={kpiQuery.data ? String(kpiQuery.data.cases_today) : undefined}
          delta={todayDelta}
          hint={
            kpiQuery.data
              ? `vs ${kpiQuery.data.cases_yesterday} ontem`
              : undefined
          }
          loading={kpiQuery.isLoading}
        />
        <KPICard
          label="Cases ontem"
          value={
            kpiQuery.data ? String(kpiQuery.data.cases_yesterday) : undefined
          }
          loading={kpiQuery.isLoading}
        />
        <KPICard
          label="Sucesso 7d"
          value={kpiQuery.data ? pct(kpiQuery.data.success_rate_7d) : undefined}
          delta={successDelta}
          hint={
            kpiQuery.data
              ? `30d avg: ${pct(kpiQuery.data.success_rate_30d_avg)}`
              : undefined
          }
          loading={kpiQuery.isLoading}
        />
        <KPICard
          label="Tempo médio"
          value={
            kpiQuery.data?.avg_processing_time_s != null
              ? `${kpiQuery.data.avg_processing_time_s.toFixed(0)}s`
              : '—'
          }
          hint="Phase 2 plumbará a duração real"
          loading={kpiQuery.isLoading}
        />
      </Grid>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        <Card className="lg:col-span-8">
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="pointer-events-none absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              <input
                type="search"
                placeholder="Buscar por filename ou mensagem…"
                value={searchInput}
                onChange={(e) => {
                  setSearchInput(e.target.value);
                  setPage(1);
                }}
                className="w-full rounded-md border border-slate-700 bg-slate-950 py-1.5 pl-8 pr-2 text-sm text-slate-100 focus:border-blue-500 focus:outline-none"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              className="rounded-md border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm text-slate-200"
            >
              <option value="">Todos os status</option>
              <option value="DONE">DONE</option>
              <option value="PROCESSING">PROCESSING</option>
              <option value="QUEUED">QUEUED</option>
              <option value="ERROR">ERROR</option>
              <option value="PENDING">PENDING</option>
            </select>

            <label
              className={`inline-flex cursor-pointer items-center gap-1.5 rounded-md border px-2 py-1.5 text-xs ${
                useGlobalRange
                  ? 'border-blue-500/40 bg-blue-600/20 text-blue-200'
                  : 'border-slate-700 text-slate-300 hover:bg-slate-800'
              }`}
              title="Usar a janela global do header como filtro de criação do caso"
            >
              <input
                type="checkbox"
                checked={useGlobalRange}
                onChange={(e) => setUseGlobalRange(e.target.checked)}
                className="h-3 w-3 accent-blue-500"
              />
              período global
            </label>
            <input
              type="datetime-local"
              value={dateFrom}
              disabled={useGlobalRange}
              onChange={(e) => setDateFrom(e.target.value)}
              className="rounded-md border border-slate-700 bg-slate-950 px-2 py-1.5 text-xs text-slate-200 disabled:opacity-50"
              aria-label="Data inicial"
            />
            <input
              type="datetime-local"
              value={dateTo}
              disabled={useGlobalRange}
              onChange={(e) => setDateTo(e.target.value)}
              className="rounded-md border border-slate-700 bg-slate-950 px-2 py-1.5 text-xs text-slate-200 disabled:opacity-50"
              aria-label="Data final"
            />

            <FilterChip
              label="tem PDF"
              active={hasPdf === true}
              onClick={() => setHasPdf(hasPdf === true ? undefined : true)}
            />
            <FilterChip
              label="tem PJc"
              active={hasPjc === true}
              onClick={() => setHasPjc(hasPjc === true ? undefined : true)}
            />
            <FilterChip
              label="só erros"
              active={hasErrors === true}
              onClick={() => setHasErrors(hasErrors === true ? undefined : true)}
            />
          </div>

          {selected.size > 0 && (
            <div className="mb-3 flex items-center justify-between rounded-md border border-blue-900/40 bg-blue-950/20 px-3 py-2 text-sm">
              <span className="text-blue-200">
                {selected.size} selecionado(s)
              </span>
              <div className="space-x-2">
                <button
                  type="button"
                  onClick={() => setSelected(new Set())}
                  className="text-xs text-slate-400 hover:text-slate-200"
                >
                  limpar
                </button>
                <button
                  type="button"
                  onClick={() => void bulkRerun()}
                  className="rounded-md bg-blue-600 px-3 py-1 text-xs font-medium text-white hover:bg-blue-500"
                >
                  Rerun selecionados
                </button>
              </div>
            </div>
          )}

          {listQuery.isLoading && <LoadingSkeleton rows={6} />}

          {listQuery.isError && (
            <div className="rounded border border-red-900/40 bg-red-950/30 p-3 text-sm text-red-300">
              Falha ao carregar cases.
            </div>
          )}

          {!listQuery.isLoading && !listQuery.isError && (
            <CasesTable
              items={items}
              selected={selected}
              onToggleSelect={toggleSelect}
              onPageChange={setPage}
              page={page}
              total={total}
              lastPage={lastPage}
            />
          )}
        </Card>

        <Card className="lg:col-span-4">
          <Title>Top errors (7d)</Title>
          {kpiQuery.isLoading && <LoadingSkeleton rows={5} />}
          {!kpiQuery.isLoading && (kpiQuery.data?.top_errors_7d?.length ?? 0) === 0 && (
            <Text>Sem erros recentes — bom sinal.</Text>
          )}
          <ul className="mt-2 space-y-2">
            {(kpiQuery.data?.top_errors_7d ?? []).map((err) => (
              <li
                key={err.error}
                className="flex items-baseline justify-between gap-3 rounded border border-slate-800 bg-slate-900/30 p-2"
              >
                <button
                  type="button"
                  onClick={() => {
                    setSearchInput(err.error.slice(0, 40));
                    setHasErrors(true);
                  }}
                  className="flex-1 text-left text-xs text-slate-300 hover:text-blue-300"
                  title="Clique para filtrar a lista por essa mensagem"
                >
                  {err.error}
                </button>
                <span className="rounded bg-red-600/20 px-2 py-0.5 text-xs font-medium text-red-300">
                  {err.count}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      </div>
    </div>
  );
}

function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-md border px-2 py-1 text-xs ${
        active
          ? 'border-blue-500 bg-blue-600/20 text-blue-200'
          : 'border-slate-700 text-slate-300'
      }`}
    >
      {label}
    </button>
  );
}

interface TableProps {
  items: CaseListItem[];
  selected: Set<number>;
  onToggleSelect: (id: number) => void;
  page: number;
  total: number;
  lastPage: number;
  onPageChange: (n: number) => void;
}

function CasesTable({
  items,
  selected,
  onToggleSelect,
  page,
  total,
  lastPage,
  onPageChange,
}: TableProps) {
  if (items.length === 0) {
    return (
      <div className="rounded border border-dashed border-slate-700 bg-slate-900/30 p-6 text-center text-sm text-slate-400">
        Nenhum case encontrado para os filtros atuais.
      </div>
    );
  }
  return (
    <div>
      <div className="overflow-x-auto rounded-lg border border-slate-800">
        <table className="min-w-full text-left text-sm text-slate-200">
          <thead className="bg-slate-900/60 text-xs uppercase text-slate-400">
            <tr>
              <th className="px-2 py-2 w-8" />
              <th className="px-3 py-2 font-medium">ID</th>
              <th className="px-3 py-2 font-medium">User</th>
              <th className="px-3 py-2 font-medium">Filename</th>
              <th className="px-3 py-2 font-medium">Status</th>
              <th className="px-3 py-2 font-medium text-center">Feedback</th>
              <th className="px-3 py-2 font-medium text-center">A/B</th>
              <th className="px-3 py-2 font-medium text-center">PDF</th>
              <th className="px-3 py-2 font-medium text-center">PJc</th>
              <th className="px-3 py-2 font-medium text-center">Memória</th>
              <th className="px-3 py-2 font-medium">Criado</th>
            </tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id} className="border-t border-slate-800 hover:bg-slate-900/40">
                <td className="px-2 py-2">
                  <input
                    type="checkbox"
                    checked={selected.has(c.id)}
                    onChange={() => onToggleSelect(c.id)}
                    aria-label={`Select case ${c.id}`}
                  />
                </td>
                <td className="px-3 py-2">
                  <Link to={`/cases/${c.id}` as never} className="text-blue-300 hover:underline">
                    {c.id}
                  </Link>
                </td>
                <td className="px-3 py-2 text-xs text-slate-400">
                  {c.user_id ? (
                    <Link to={`/users/${c.user_id}` as never} className="hover:underline">
                      {c.user_email ?? `#${c.user_id}`}
                    </Link>
                  ) : (
                    '—'
                  )}
                </td>
                <td className="px-3 py-2">{c.filename ?? '—'}</td>
                <td className="px-3 py-2">
                  <StatusBadge status={(c.status ?? '').toLowerCase()} />
                </td>
                <td className="px-3 py-2 text-center" title={c.thumb === 'up' ? 'Usuário curtiu o cálculo' : c.thumb === 'down' ? 'Usuário NÃO curtiu o cálculo' : 'Sem feedback'}>
                  {c.thumb === 'up' ? (
                    <span className="text-emerald-400">👍</span>
                  ) : c.thumb === 'down' ? (
                    <span className="text-rose-400">👎</span>
                  ) : (
                    <span className="text-slate-600">—</span>
                  )}
                </td>
                <td className="px-3 py-2 text-center text-xs text-slate-400">
                  {c.has_pjc ? '✓' : '·'} / {c.has_pdf ? '✓' : '·'}
                </td>
                <td className="px-3 py-2 text-center">
                  {c.has_pdf ? (
                    <span className="rounded bg-emerald-600/20 px-1.5 py-0.5 text-xs text-emerald-300">
                      ok
                    </span>
                  ) : (
                    <span className="text-slate-600">—</span>
                  )}
                </td>
                <td className="px-3 py-2 text-center">
                  {c.has_pjc ? (
                    <span className="rounded bg-emerald-600/20 px-1.5 py-0.5 text-xs text-emerald-300">
                      ok
                    </span>
                  ) : (
                    <span className="text-slate-600">—</span>
                  )}
                </td>
                <td className="px-3 py-2 text-center" title={c.baixou_memoria ? 'Usuário baixou a Memória de Cálculo (laudo)' : 'Não baixou a Memória'}>
                  {c.baixou_memoria ? (
                    <span className="rounded bg-violet-600/20 px-1.5 py-0.5 text-xs text-violet-300">📄 ✓</span>
                  ) : (
                    <span className="text-slate-600">—</span>
                  )}
                </td>
                <td className="px-3 py-2 text-xs text-slate-400">
                  {fmtDate(c.created_at)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-3 flex items-center justify-between text-xs text-slate-400">
        <span>
          Página {page} de {lastPage} — total {total}
        </span>
        <div className="space-x-2">
          <button
            type="button"
            disabled={page <= 1}
            onClick={() => onPageChange(Math.max(1, page - 1))}
            className="rounded border border-slate-700 px-2 py-1 disabled:opacity-40"
          >
            ←
          </button>
          <button
            type="button"
            disabled={page >= lastPage}
            onClick={() => onPageChange(Math.min(lastPage, page + 1))}
            className="rounded border border-slate-700 px-2 py-1 disabled:opacity-40"
          >
            →
          </button>
        </div>
      </div>
    </div>
  );
}
